<template>
  <v-container fluid>
    <div class="my-tasks-page">
      <!-- <h2 class="page-title">My Tasks</h2> -->
      <!-- Action Bar: Search & Reload -->
      <v-row class="action-bar d-flex align-center mb-2" no-gutters>
        <v-col cols="12" sm="6" class="search-bar d-flex align-center">
          <v-text-field
            v-model="search"
            label="Search by AASB Ref or Vehicle Number"
            placeholder="Type AASB Ref or Vehicle Number"
            prepend-icon="mdi-magnify"
            clearable
            dense
            class="search-input"
            color="primary"
          />
        </v-col>
        <v-col
          cols="12"
          sm="6"
          class="reload-bar d-flex align-center justify-end"
        >
          <v-btn
            color="success"
            class="reload-btn"
            large
            elevation="2"
            @click="onReload"
          >
            <v-icon left large>mdi-refresh</v-icon>
            Reload
          </v-btn>
          <v-btn
            v-if="showAcceptAssignment"
            color="deep-orange accent-4"
            class="accept-btn ml-3"
            large
            elevation="2"
            @click="acceptSelectedAssignments"
          >
            <v-icon left large>mdi-check-circle</v-icon>
            Accept Assignment
          </v-btn>
        </v-col>
      </v-row>
      <!-- Filter Controls -->
      <v-card class="pa-4 mb-4 elevation-2 filter-card" outlined>
        <v-row dense>
          <v-col cols="12" sm="6" md="3">
            <v-select
              v-model="filters.fileStatus"
              :items="statusOptions"
              item-text="name"
              item-value="stageCode"
              label="Status"
              clearable
              dense
              @change="onFilterChange"
              prepend-icon="mdi-checkbox-marked-circle-outline"
              color="primary"
            />
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-select
              v-model="sortBy"
              :items="sortFields"
              label="Sort by"
              dense
              clearable
              @change="onSortChange"
              prepend-icon="mdi-sort"
              color="primary"
            />
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-select
              v-model="sortDesc"
              :items="orderOptions"
              label="Order"
              dense
              clearable
              @change="onSortChange"
              prepend-icon="mdi-sort"
              color="primary"
            />
          </v-col>
          <v-col cols="12" sm="6" md="3" class="d-flex align-end">
            <v-btn
              text
              small
              class="clear-btn-ux"
              @click="clearFilters"
              color="primary"
            >
              <v-icon left small color="grey">mdi-filter-remove</v-icon>
              <span class="clear-btn-label">Clear</span>
            </v-btn>
          </v-col>
        </v-row>
      </v-card>
      <v-card class="pa-4 mb-4 table-container elevation-2" outlined>
        <!-- Top Pagination Bar -->
        <v-row
          class="pagination-bar pagination-bar-top d-flex align-center justify-space-between flex-wrap"
          no-gutters
        >
          <v-col
            cols="12"
            sm="8"
            class="pagination-controls d-flex align-center flex-wrap"
          >
            <v-select
              :items="[30, 50, 100]"
              v-model="pageSize"
              label="Rows"
              dense
              hide-details
              style="max-width: 90px"
              @change="onPageSizeChange"
              class="pagination-select"
              color="primary"
            ></v-select>
            <v-btn
              icon
              :disabled="page === 1"
              @click="goToFirstPage"
              class="pagination-btn"
              color="primary"
            >
              <v-icon small>mdi-page-first</v-icon>
            </v-btn>
            <v-btn
              icon
              :disabled="page === 1"
              @click="goToPrevPage"
              class="pagination-btn"
              color="primary"
            >
              <v-icon small>mdi-chevron-left</v-icon>
            </v-btn>
            <template v-for="btn in paginationButtons">
              <v-btn
                v-if="btn.type === 'page'"
                :key="'page-top-' + btn.page"
                small
                :color="btn.page === page ? 'primary' : ''"
                @click="setPage(btn.page)"
                class="mx-1 pagination-btn"
              >
                {{ btn.page }}
              </v-btn>
              <span
                v-else-if="btn.type === 'ellipsis'"
                :key="'ellipsis-top-' + btn.key"
                class="mx-1 pagination-ellipsis"
                >...</span
              >
            </template>
            <v-btn
              icon
              :disabled="page === pageCount"
              @click="goToNextPage"
              class="pagination-btn"
              color="primary"
            >
              <v-icon small>mdi-chevron-right</v-icon>
            </v-btn>
            <v-btn
              icon
              :disabled="page === pageCount"
              @click="goToLastPage"
              class="pagination-btn"
              color="primary"
            >
              <v-icon small>mdi-page-last</v-icon>
            </v-btn>
            <v-text-field
              v-model.number="jumpToPage"
              label="Go to"
              type="number"
              dense
              hide-details
              style="max-width: 60px"
              min="1"
              :max="pageCount"
              @keyup.enter="jumpPage"
              class="ml-2 pagination-jump"
              color="primary"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="4" class="ml-2 pagination-total text-right">
            {{ total }} total records
          </v-col>
        </v-row>
        <v-progress-linear
          v-if="loading"
          indeterminate
          color="primary"
          class="mb-2"
        />
        <v-data-table
          :headers="headers"
          :items="filteredTasks"
          :loading="loading"
          :items-per-page="pageSize"
          :page.sync="page"
          class="mytasks-table"
          :loading-text="'Loading tasks...'"
          :no-data-text="error ? error : 'No pending files found.'"
          dense
          disable-sort
          hide-default-footer
          color="primary"
          @click:row="viewItem"
          style="cursor: pointer"
          show-select
          :value="selected"
          @input="onSelectionChange"
        >
          <template v-slot:[`item.aasbRef`]="{ item }">
            {{ formatAasbRef(item) }}
          </template>
          <template v-slot:[`item.vehicleNo`]="{ item }">
            <span v-if="item.vehicleNo">{{
              item.vehicleNo.toUpperCase()
            }}</span>
            <span v-else>TBA</span>
          </template>
          <template v-slot:[`item.fileStatus`]="{ item }">
            <v-chip
              label
              outlined
              x-small
              :color="getStatusColor(item.fileStatus)"
              class="status-chip"
            >
              {{ getStatusName(item.fileStatus) }}
            </v-chip>
          </template>
          <template v-slot:[`item.insurerName`]="{ item }">
            <span>{{ getInsurerName(item.insurer) }}</span>
          </template>
          <template v-slot:[`item.days`]="{ item }">
            <v-chip
              label
              x-small
              :class="getDaysChipClass(item)"
              class="days-chip-v2"
              outlined
            >
              {{ formatDaysWithTat(item) }}
            </v-chip>
          </template>
        </v-data-table>
        <!-- Bottom Pagination Bar -->
        <v-row
          class="pagination-bar pagination-bar-bottom d-flex align-center justify-space-between mt-2 flex-wrap"
          no-gutters
        >
          <v-col
            cols="12"
            sm="8"
            class="pagination-controls d-flex align-center flex-wrap"
          >
            <v-select
              :items="[30, 50, 100]"
              v-model="pageSize"
              label="Rows"
              dense
              hide-details
              style="max-width: 90px"
              @change="onPageSizeChange"
              class="pagination-select"
              color="primary"
            ></v-select>
            <v-btn
              icon
              :disabled="page === 1"
              @click="goToFirstPage"
              class="pagination-btn"
              color="primary"
            >
              <v-icon small>mdi-page-first</v-icon>
            </v-btn>
            <v-btn
              icon
              :disabled="page === 1"
              @click="goToPrevPage"
              class="pagination-btn"
              color="primary"
            >
              <v-icon small>mdi-chevron-left</v-icon>
            </v-btn>
            <template v-for="btn in paginationButtons">
              <v-btn
                v-if="btn.type === 'page'"
                :key="'page-bottom-' + btn.page"
                small
                :color="btn.page === page ? 'primary' : ''"
                @click="setPage(btn.page)"
                class="mx-1 pagination-btn"
              >
                {{ btn.page }}
              </v-btn>
              <span
                v-else-if="btn.type === 'ellipsis'"
                :key="'ellipsis-bottom-' + btn.key"
                class="mx-1 pagination-ellipsis"
                >...</span
              >
            </template>
            <v-btn
              icon
              :disabled="page === pageCount"
              @click="goToNextPage"
              class="pagination-btn"
              color="primary"
            >
              <v-icon small>mdi-chevron-right</v-icon>
            </v-btn>
            <v-btn
              icon
              :disabled="page === pageCount"
              @click="goToLastPage"
              class="pagination-btn"
              color="primary"
            >
              <v-icon small>mdi-page-last</v-icon>
            </v-btn>
            <v-text-field
              v-model.number="jumpToPage"
              label="Go to"
              type="number"
              dense
              hide-details
              style="max-width: 60px"
              min="1"
              :max="pageCount"
              @keyup.enter="jumpPage"
              class="ml-2 pagination-jump"
              color="primary"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="4" class="ml-2 pagination-total text-right">
            {{ total }} total records
          </v-col>
        </v-row>
      </v-card>
    </div>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import moment from "moment";
import InsDataService from "@/services/InsDataService";
import DeptDataService from "@/services/DeptDataService";
import SubRefTypeDataService from "@/services/SubDeptDataService";
import BranchDataService from "@/services/BranchDataService";
import StagesDataService from "@/services/StageDataService";
import axios from "axios";
import { apiConfig } from "@/config/api.config";
import TatChartDataService from "@/services/TatChartDataService";

export default {
  name: "MyTasks",
  data() {
    return {
      page: 1,
      pageSize: 30,
      loading: false,
      error: "",
      jumpToPage: null,
      sortBy: "days",
      sortDesc: true,
      search: "",
      filters: {
        fileStatus: "",
      },
      selected: [],
      headers: [
        { text: "AASB Ref", value: "aasbRef", width: "220px" },
        { text: "Vehicle No.", value: "vehicleNo", width: "140px" },
        { text: "Status", value: "fileStatus", width: "120px" },
        { text: "Insurer", value: "insurerName", width: "160px" },
        { text: "Days Open", value: "days", width: "100px", align: "center" },
      ],
      insurers: [],
      departments: [],
      subRefTypes: [],
      branches: [],
      stages: [],
      tatCharts: [],
      sortFields: [
        { text: "AASB Ref", value: "aasbRef" },
        { text: "Status", value: "fileStatus" },
        { text: "Insurer", value: "insurerName" },
        { text: "Days Open", value: "days" },
      ],
      orderOptions: [
        { text: "Descending", value: true },
        { text: "Ascending", value: false },
      ],
    };
  },
  computed: {
    ...mapGetters("casefiles", [
      "getPaginatedPermittedActive",
      "getPaginatedTotalPermittedActive",
    ]),
    tasks() {
      // Only show files where current user is adjuster, editor, or clerkInCharge
      const user = this.$store.state.auth.user;
      if (!user || !user.id) return [];
      let arr = this.getPaginatedPermittedActive || [];

      arr = arr.filter(
        (item) =>
          String(item.adjuster) === String(user.id) ||
          String(item.editor) === String(user.id) ||
          String(item.clerkInCharge) === String(user.id)
      );

      // Local sort (since backend only sorts by days)
      if (this.sortBy) {
        arr = [...arr].sort((a, b) => {
          let aVal, bVal;
          switch (this.sortBy) {
            case "aasbRef":
              aVal = this.formatAasbRef(a);
              bVal = this.formatAasbRef(b);
              break;
            case "fileStatus":
              aVal = this.getStatusName(a.fileStatus);
              bVal = this.getStatusName(b.fileStatus);
              break;
            case "insurerName":
              aVal = this.getInsurerName(a.insurer);
              bVal = this.getInsurerName(b.insurer);
              break;
            case "days":
              aVal = this.getDaysOpen(a.dateOfAssign);
              bVal = this.getDaysOpen(b.dateOfAssign);
              break;
            default:
              aVal = a[this.sortBy];
              bVal = b[this.sortBy];
          }
          if (aVal == null) aVal = "";
          if (bVal == null) bVal = "";
          if (typeof aVal === "string" && typeof bVal === "string") {
            return this.sortDesc
              ? bVal.localeCompare(aVal)
              : aVal.localeCompare(bVal);
          }
          return this.sortDesc ? bVal - aVal : aVal - bVal;
        });
      }
      return arr;
    },
    filteredTasks() {
      // Custom search logic for AASB REF and vehicle number
      if (!this.search || !this.search.trim()) return this.tasks;
      const term = this.search.trim().toLowerCase();
      return this.tasks.filter((item) => {
        // AASB REF: match id or formatted ref
        const aasbRef = this.formatAasbRef(item).toLowerCase();
        const idStr = String(item.id).toLowerCase();
        // Vehicle number: match vehicleNo
        const vehicleNo = (item.vehicleNo || "").toLowerCase();
        return (
          aasbRef.includes(term) ||
          idStr.includes(term) ||
          vehicleNo.includes(term)
        );
      });
    },
    total() {
      // Show the filtered count
      return this.tasks.length;
    },
    pageCount() {
      return Math.ceil(this.total / this.pageSize) || 1;
    },
    statusOptions() {
      // Only show statuses present in current filtered tasks
      const codes = Array.from(
        new Set((this.tasks || []).map((t) => t.fileStatus).filter(Boolean))
      );
      const options = codes
        .map((code) => {
          const stage = this.stages.find((s) => s.stageCode === code);
          return {
            stageCode: code,
            name: stage ? stage.name : code,
          };
        })
        .sort((a, b) => a.name.localeCompare(b.name));
      return [{ stageCode: "", name: "All Statuses" }, ...options];
    },
    paginationButtons() {
      const pages = [];
      const total = this.pageCount;
      const current = this.page;
      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          pages.push({ type: "page", page: i });
        }
      } else {
        if (current <= 4) {
          for (let i = 1; i <= 5; i++) pages.push({ type: "page", page: i });
          pages.push({ type: "ellipsis", key: "e1" });
          pages.push({ type: "page", page: total });
        } else if (current >= total - 3) {
          pages.push({ type: "page", page: 1 });
          pages.push({ type: "ellipsis", key: "e2" });
          for (let i = total - 4; i <= total; i++)
            pages.push({ type: "page", page: i });
        } else {
          pages.push({ type: "page", page: 1 });
          pages.push({ type: "ellipsis", key: "e3" });
          for (let i = current - 1; i <= current + 1; i++)
            pages.push({ type: "page", page: i });
          pages.push({ type: "ellipsis", key: "e4" });
          pages.push({ type: "page", page: total });
        }
      }
      return pages;
    },
    showAcceptAssignment() {
      // Show button only if all selected rows are ADAC and at least one is selected
      return (
        this.selected &&
        this.selected.length > 0 &&
        this.selected.every((item) => item.fileStatus === "ADAC")
      );
    },
  },
  methods: {
    ...mapActions("casefiles", ["fetchPaginatedPermittedActive"]),
    onReload() {
      // Reset filters, reload, and clear selection
      this.clearFilters();
      this.selected = [];
    },
    async loadTasks() {
      this.loading = true;
      this.error = "";
      try {
        await this.fetchPaginatedPermittedActive({
          page: this.page,
          pageSize: this.pageSize,
          ...this.getFiltersForFetch(), // Spread filters at top level
          myTasks: true, // Top-level property
        });
      } catch (e) {
        this.error = "Failed to load tasks.";
      }
      this.loading = false;
    },
    onRefresh() {
      // Reset filters and reload
      this.clearFilters();
    },
    handlePageChange(newPage) {
      this.page = newPage;
      this.loadTasks();
    },
    onPageSizeChange() {
      this.page = 1;
      this.loadTasks();
    },
    setPage(page) {
      if (page !== this.page) {
        this.page = page;
        this.loadTasks();
      }
    },
    goToFirstPage() {
      if (this.page > 1) {
        this.setPage(1);
      }
    },
    goToPrevPage() {
      if (this.page > 1) {
        this.setPage(this.page - 1);
      }
    },
    goToNextPage() {
      if (this.page < this.pageCount) {
        this.setPage(this.page + 1);
      }
    },
    goToLastPage() {
      if (this.page < this.pageCount) {
        this.setPage(this.pageCount);
      }
    },
    jumpPage() {
      if (
        this.jumpToPage &&
        this.jumpToPage >= 1 &&
        this.jumpToPage <= this.pageCount
      ) {
        this.setPage(this.jumpToPage);
      }
    },
    clearFilters() {
      this.filters.fileStatus = "";
      this.sortBy = "days";
      this.sortDesc = true;
      this.page = 1;
      this.loadTasks();
    },
    onFilterChange() {
      this.page = 1;
      this.loadTasks();
    },
    onSortChange() {
      // Only local sort, so just refresh table
      // (If backend sort is needed, pass sortBy/sortDesc to backend)
    },
    onSelectionChange(val) {
      this.selected = val;
    },
    async acceptSelectedAssignments() {
      // Only accept if all selected are ADAC
      const idsToAccept = this.selected
        .filter((item) => item.fileStatus === "ADAC")
        .map((item) => item.id);
      if (!idsToAccept.length || this.selected.length !== idsToAccept.length)
        return;
      try {
        await axios.post(`${apiConfig.baseURL}/casefiles/accept-assignment`, {
          ids: idsToAccept,
        });
        this.$store.dispatch("setSnackbar", {
          showing: true,
          color: "success",
          text: "Assignment(s) accepted.",
        });
        // Clear selection and reset status filter before reload
        this.selected = [];
        this.filters.fileStatus = "";
        this.page = 1;
        await this.loadTasks();
      } catch (e) {
        this.$store.dispatch("setSnackbar", {
          showing: true,
          color: "error",
          text: "Failed to accept assignment(s).",
        });
      }
    },
    getFiltersForFetch() {
      // Only send fileStatus if set
      const filters = {};
      if (this.filters.fileStatus) filters.fileStatus = this.filters.fileStatus;
      return filters;
    },
    // --- Display helpers (copied/adapted from Home.vue/MyTasks-20251209.vue) ---
    formatAasbRef(item) {
      const dept = this.departments.find((d) => d.id === item.refType);
      const sub = this.subRefTypes.find((s) => s.id === item.subRefType);
      const branch = this.branches.find((b) => b.id === item.branch);
      const year =
        item.dateOfAssign || item.createdAt
          ? moment(item.dateOfAssign || item.createdAt).format("YY")
          : "";
      return [
        "AA",
        dept ? dept.name : item.refType,
        sub ? sub.subCode : item.subRefType,
        item.id,
        year,
        branch ? branch.brCode : item.branch,
      ]
        .filter(Boolean)
        .join("/");
    },
    getStatusName(code) {
      const stage = this.stages.find((s) => s.stageCode === code);
      return stage ? stage.name : code;
    },
    getStatusColor(code) {
      // Example logic from MyTasks-20251209.vue
      switch (code) {
        case "NEW":
        case "INVE":
          return "orange lighten-2";
        case "INP":
        case "PEDI":
          return "blue lighten-1";
        case "SUS":
        case "ADAC":
          return "red lighten-1";
        // case "APR":
        // case "CLO":
        //   return "green lighten-2";
        default:
          return "green lighten-2";
      }
    },
    getInsurerName(id) {
      const ins = this.insurers.find((i) => i.id === id);
      return ins ? ins.name : id;
    },
    // days since dateOfAssign (integer)
    getDaysOpen(dateOfAssign) {
      if (!dateOfAssign) return 0;
      return moment().diff(moment(dateOfAssign), "days");
    },
    // color based on tat values (item passed for insurer/subRef)
    getDaysColor(item) {
      const days = this.getDaysOpen(item.dateOfAssign);
      const tat = this.getTatValues(item.insurer, item.subRefType);

      // If TAT exists, use tat thresholds (darker orange for tatMax)
      if (tat && tat.tatMax) {
        if (days >= tat.tatMax) return "orange darken-2";
        if (tat.tatAlert && days >= tat.tatAlert) return "orange lighten-2";
        return "grey lighten-3";
      }

      // Fallback previous logic if no tat
      if (days >= 30) return "red lighten-2";
      if (days >= 14) return "orange lighten-2";
      if (days >= 7) return "yellow lighten-2";
      return "grey lighten-3";
    },
    // format display: "25 Days (TAT 30)" or "25 Days"
    formatDaysWithTat(item) {
      const days = this.getDaysOpen(item.dateOfAssign);
      const tat = this.getTatValues(item.insurer, item.subRefType);
      if (tat && tat.tatMax) {
        return `${days} Days (TAT ${tat.tatMax})`;
      }
      return `${days} Days`;
    },
    async loadTatCharts() {
      try {
        const res = await TatChartDataService.getAll();
        this.tatCharts = res.data || [];
      } catch (e) {
        console.error("Failed to load TAT charts:", e);
      }
    },
    // New helper: return { tatAlert, tatMax } or null
    getTatValues(insId, subRefTypeId) {
      if (!this.tatCharts || !insId || !subRefTypeId) return null;
      const t = this.tatCharts.find(
        (x) =>
          String(x.insId) === String(insId) &&
          String(x.subDeptId) === String(subRefTypeId)
      );
      if (!t) return null;
      return {
        tatAlert: parseInt(t.tatAlert, 10) || null,
        tatMax: parseInt(t.tatMax, 10) || null,
      };
    },
    viewItem(item) {
      this.$router.push({
        name: "vuexFile",
        params: { id: item.id },
      });
    },
    getDaysChipClass(item) {
      const days = this.getDaysOpen(item.dateOfAssign);
      const tat = this.getTatValues(item.insurer, item.subRefType);
      if (tat && tat.tatMax) {
        if (days >= tat.tatMax) return "days-chip-overdue";
        if (tat.tatAlert && days >= tat.tatAlert) return "days-chip-alert";
        return "days-chip-tat";
      }
      if (days >= 30) return "days-chip-overdue";
      if (days >= 14) return "days-chip-alert";
      if (days >= 7) return "days-chip-tat";
      return "days-chip-default";
    },
  },
  async mounted() {
    // --- Departments, Sub-Ref Types, Branches, Stages (for dropdowns and display) ---
    const deptRes = await DeptDataService.getAll();
    this.departments = deptRes.data || [];
    const subRes = await SubRefTypeDataService.getAll();
    this.subRefTypes = subRes.data || [];
    const branchRes = await BranchDataService.getAll();
    this.branches = branchRes.data || [];
    const stageRes = await StagesDataService.getAll();
    this.stages = stageRes.data || [];
    // --- Insurers (separate request) ---
    const insRes = await InsDataService.getAll();
    this.insurers = insRes.data || [];
    // --- TAT Charts (separate request) ---
    await this.loadTatCharts();
    // --- Initial load ---
    this.loadTasks();
  },
};
</script>

<style scoped>
.v-container {
  padding-top: 16px !important;
  padding-bottom: 16px !important;
  background: #f5f8fa;
  min-height: 100vh;
}
.my-tasks-page {
  /* max-width: 1200px; */
  margin: 0 auto;
  padding: 0 8px;
}
.page-title {
  font-size: 2em;
  font-weight: 700;
  margin-bottom: 24px;
  color: #1976d2;
  letter-spacing: 0.5px;
}
.action-bar {
  margin-bottom: 12px;
  padding: 0 2px;
  gap: 16px;
}
.search-bar {
  flex: 1 1 320px;
  max-width: 400px;
}
.reload-bar {
  flex: 0 0 auto;
  margin-left: 24px;
}
.reload-btn {
  min-width: 140px;
  font-weight: 700;
  font-size: 1.1em;
  letter-spacing: 0.5px;
  border-radius: 24px;
  box-shadow: 0 2px 8px rgba(56, 142, 60, 0.12);
  transition: box-shadow 0.2s;
}
.reload-btn:hover {
  box-shadow: 0 4px 16px rgba(56, 142, 60, 0.18);
}
.filter-card {
  background: #f8fafc;
  border-radius: 8px;
  box-shadow: 0 2px 8px #f0f1f2;
}
.table-container {
  background: #fff;
  border-radius: 8px;
  padding: 0;
  box-shadow: 0 2px 8px #f0f1f2;
  margin-bottom: 24px;
  margin-top: 8px;
  overflow-x: auto;
}
.mytasks-table {
  border-radius: 8px;
  background: #f8fafc;
  min-width: 800px;
}
.status-chip {
  font-weight: 600;
  font-size: 13px;
  letter-spacing: 0.5px;
}
.days-chip-v2 {
  font-weight: 500;
  font-size: 13px;
  letter-spacing: 0.2px;
  border-radius: 16px;
  border: 1px solid #e0e0e0;
  padding: 0 10px;
  min-width: 80px;
  background: #f8fafc;
  color: #222;
  transition: background 0.2s, color 0.2s;
}
.days-chip-tat {
  background: #e3f2fd !important;
  color: #1565c0 !important;
  border-color: #90caf9 !important;
}
.days-chip-alert {
  background: #fff3e0 !important;
  color: #ef6c00 !important;
  border-color: #ffb74d !important;
}
.days-chip-overdue {
  background: #ffebee !important;
  color: #c62828 !important;
  border-color: #ef9a9a !important;
}
.days-chip-default {
  background: #f5f5f5 !important;
  color: #616161 !important;
  border-color: #e0e0e0 !important;
}
.pagination-bar {
  min-height: 64px;
  flex-wrap: wrap;
  gap: 8px;
  margin-left: 0;
  margin-right: 0;
  margin-top: 16px;
  margin-bottom: 16px;
}
.pagination-bar-top {
  margin-bottom: 0;
  margin-top: 0;
}
.pagination-bar-bottom {
  margin-top: 0;
  margin-bottom: 0;
}
.pagination-controls {
  flex-wrap: wrap;
  gap: 4px;
}
.pagination-btn {
  min-width: 32px;
  height: 32px;
  padding: 0 4px;
}
.pagination-select {
  min-width: 70px;
  max-width: 90px;
}
.pagination-jump {
  min-width: 50px;
  max-width: 60px;
}
.pagination-total {
  font-size: 15px;
  white-space: nowrap;
}
.pagination-ellipsis {
  font-size: 18px;
  color: #888;
}
.search-input {
  max-width: 320px;
  min-width: 180px;
}
/* --- Responsive table for mobile --- */
@media (max-width: 900px) {
  .table-container {
    padding: 0 2px;
    overflow-x: auto;
  }
  .mytasks-table {
    min-width: 600px;
    font-size: 12px;
  }
}
@media (max-width: 600px) {
  .my-tasks-page {
    max-width: 100vw;
    padding: 0 2px;
  }
  .page-title {
    font-size: 1.3em;
    margin-bottom: 16px;
  }
  .filter-card,
  .table-container {
    padding: 0;
    margin-bottom: 12px;
    border-radius: 0;
    box-shadow: none;
  }
  .table-container {
    overflow-x: auto;
    width: 100vw;
    min-width: 0;
  }
  .mytasks-table {
    min-width: 0;
    width: 100vw;
    font-size: 12px;
  }
  .pagination-bar {
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 4px;
  }
  .pagination-controls {
    flex-wrap: wrap;
    gap: 2px;
    margin-bottom: 4px;
  }
  .pagination-btn {
    min-width: 28px;
    height: 28px;
    font-size: 13px;
    padding: 0 2px;
  }
  .pagination-select {
    min-width: 60px;
    max-width: 70px;
    font-size: 13px;
  }
  .pagination-jump {
    min-width: 40px;
    max-width: 50px;
    font-size: 13px;
  }
  .pagination-total {
    font-size: 13px;
    margin-left: 0 !important;
    text-align: left !important;
  }
  .pagination-ellipsis {
    font-size: 16px;
  }
  .search-bar,
  .reload-bar {
    margin-left: 0 !important;
    margin-bottom: 8px;
    justify-content: flex-start !important;
  }
  .reload-btn,
  .accept-btn {
    min-width: 100px;
    font-size: 0.95em;
    padding: 0 8px;
    margin-left: 0 !important;
    margin-top: 8px;
  }
}
.clear-btn-ux {
  background-color: #e3f2fd !important;
  color: #1976d2 !important;
  font-style: italic;
  font-size: 13px;
  min-width: 70px;
  box-shadow: none !important;
  border-radius: 16px;
  transition: background 0.2s;
}
.clear-btn-ux:hover {
  background-color: #bbdefb !important;
}
.clear-btn-label {
  font-style: italic;
  color: #1976d2;
}
.accept-btn {
  min-width: 180px;
  font-weight: 700;
  font-size: 1.1em;
  letter-spacing: 0.5px;
  border-radius: 24px;
  box-shadow: 0 2px 8px rgba(255, 87, 34, 0.12);
  transition: box-shadow 0.2s;
  color: #fff !important;
}
.accept-btn:hover {
  box-shadow: 0 4px 16px rgba(255, 87, 34, 0.18);
}
</style>
